import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { BarLayoutHttpParams } from '@lvflanders/bar-ng-ui';
import { DidoCoreStateService } from '@lvflanders/digitaaldossier-ng';

import { DIDO_DOCUMENTEN_CONTROL_CONSTANTS } from '../dido-documenten-control.constants';
import { DocumentModel } from '../types/dido-document-model.type';
import { DocumentenSaveModel } from '../types/dido-document-save-model.type';
import { DidoDocumentenRequestModel } from '../types/dido-documenten-request-model.type';
import { DidoDocumenttypeOption } from '../types/dido-documenttype-option.type';
import { DocumenttypeDossierAndTaakInfoDto } from '../types/dido-dossier-and-taak-info.type';
import { DidoResultModel } from '../types/dido-result-model.type';
import { DocumenttypeModel } from '../types/documenttype-model.type';

@Injectable()
export class DidoDocumentenService {
    readonly #domSanitizer = inject(DomSanitizer);
    readonly #state = inject(DidoCoreStateService);
    readonly #http = inject(HttpClient);

    /** Gets the documenten */
    getDocumenten(request: DidoDocumentenRequestModel, showLoader): Observable<DidoResultModel> {
        let documenten$;
        const params = new BarLayoutHttpParams(showLoader != undefined ? showLoader : true);
        if (request.documentnummers && !request.toepassingCode && !request.dossiernummer) {
            documenten$ = this.#http.post<DidoResultModel>(this.#state.didoWebUrl + 'api/documenten/getbydocnummers', request, { params: params });
        } else {
            documenten$ = this.#http.post<DidoResultModel>(this.#state.didoWebUrl + 'api/documenten/getbycontext', request, { params: params });
        }
        return this.#handleDocumenten(documenten$);
    }

    getDossiersEnTaakInfo(documentType): Observable<DocumenttypeDossierAndTaakInfoDto> {
        const params = new HttpParams();
        params.set('documenttypeCode', documentType);
        return this.#http.get<DocumenttypeDossierAndTaakInfoDto>(this.#state.didoWebUrl + 'api/documenten/getdossierandtaakInfo', {
            params: { documenttypeCode: documentType }
        });
    }

    /** Gets the document types for the documenten control */
    getDocumenttypes(documenttypeOptions: DidoDocumenttypeOption[] | undefined, showLoader): Observable<DocumenttypeModel[]> {
        // get selectable documenttype codes (and pass the options chosen by the user)
        if (!documenttypeOptions) documenttypeOptions = [];
        return this.#http.post<DocumenttypeModel[]>(this.#state.didoWebUrl + 'api/documenten/documenttypes', documenttypeOptions, {
            params: new BarLayoutHttpParams(showLoader)
        });
    }

    /** Saves the documenten */
    save(request: DocumentenSaveModel, showLoader): Observable<DidoResultModel> {
        const params = new BarLayoutHttpParams(showLoader != undefined ? showLoader : true);
        const documenten$ = this.#http.post<DidoResultModel>(this.#state.didoWebUrl + 'api/documenten/submit', request, { params: params });
        return this.#handleDocumenten(documenten$);
    }

    getContent(documentnummer: number, documentnaam: string, convert: boolean): any {
        return this.#http.get<any>(this.#state.didoWebUrl + 'api/documenten/getdocumentcontent', {
            params: { documentnummer: documentnummer, documentnaam: documentnaam, convert: convert }
        });
    }

    getMailPreview(documentnummer: number, filename: string): Observable<any> {
        return this.#http.get(this.#state.didoWebUrl + 'api/mailpreview/get-preview', {
            params: { documentnummer: documentnummer, filename: filename },
            responseType: 'text'
        });
    }

    getMailPreviewContent(content: string, filename: string): Observable<any> {
        return this.#http.post(
            this.#state.didoWebUrl + 'api/mailpreview/get-preview-content',
            { content: content, filename: filename },
            { responseType: 'text' }
        );
    }

    getPdfAsBlob(url) {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/pdf');
        return this.#http.get(url, { headers: headers, responseType: 'blob' });
    }

    downloadAllDocuments(documents: { documentnummer: number; bestandsnaam: string }[]): Observable<Blob> {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/zip');
        return this.#http.post(this.#state.didoWebUrl + 'api/documenten/downloadalldocuments', documents, {
            headers: headers,
            responseType: 'blob'
        });
    }

    /**
     *  Enriches a document model with extra client side logic.
     * @param document
     */
    enrichDocument(document: any): void {
        if (document.emailBijlages && document.emailBijlages.length > 0) {
            document.emailBijlages.forEach((b) => this.enrichDocument(b));
        }

        this.#enrichExtension(document);
    }

    #handleDocumenten(documenten$: Observable<DidoResultModel>): Observable<DidoResultModel> {
        return documenten$.pipe(
            tap((result) => {
                if (result.documenten) {
                    result.documenten.forEach((document) => {
                        this.enrichDocument(document);
                    });
                }
            })
        );
    }

    #enrichExtension(document: DocumentModel): void {
        let bestandsextensie = document.bestandsextensie;
        if (bestandsextensie != null) {
            bestandsextensie = bestandsextensie.toLowerCase();
        }

        const mapping = DIDO_DOCUMENTEN_CONTROL_CONSTANTS.ALLOWED_EXTENSION_MAPPINGS.find((mapping) => mapping.extension == bestandsextensie);
        if (mapping != null) {
            document.fontIcon = mapping.fontIcon;
        } else {
            document.fontIcon = 'fa-file';
        }

        if (document.documentnummer != null) {
            document.safeOri = this.#domSanitizer.bypassSecurityTrustResourceUrl(document.ori);
        }
    }
}
